/**
 * Formatters shared by the Management surfaces.
 *
 * Gap A phase 2: these lived inside `AdsManagementTab`, so a block that used
 * them could only render inside that component. The keywords table needed
 * all four to move to the Keyword Review tab, and the summary tiles still
 * need two of them, so they belong in neither caller.
 *
 * Pure functions only. Nothing here reads a query, a route or a store.
 */
import type React from 'react';

import type { ChangeHistoryResponse } from '@/services/googleAdsManagementService';

export function fmtMoney(n: number): string {
  return `$${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function fmtConv(n: number): string {
  return (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const QUALITY_RANK: Record<string, number> = {
  BELOW_AVERAGE: 1,
  AVERAGE: 2,
  ABOVE_AVERAGE: 3,
};

const QUALITY_LABELS: Record<string, string> = {
  BELOW_AVERAGE: 'Below avg',
  AVERAGE: 'Average',
  ABOVE_AVERAGE: 'Above avg',
};

/** Sort key for a quality bucket; 0 for unscored so it sinks. */
export function qualityRank(raw: string | null | undefined): number {
  if (!raw || raw === 'UNKNOWN') return 0;
  return QUALITY_RANK[raw] ?? 0;
}

export function qualityLabel(raw: string): React.ReactNode {
  // ⚠ Google's QualityScoreBucket enum is UNSPECIFIED=0, UNKNOWN=1,
  // BELOW_AVERAGE=2… — _enum_name_or_none only filters UNSPECIFIED, so
  // UNKNOWN (a real value for an unscored keyword) reaches here. Treat it
  // the same as unset rather than printing the raw constant (Viktor, #2045).
  if (!raw || raw === 'UNKNOWN') return <span className="text-muted-foreground">—</span>;
  return QUALITY_LABELS[raw] ?? raw;
}

/**
 * "Net daily budget +$310 across 4 campaigns" beats "27 budget changes".
 *
 * ⚠ Falls back to the count when Google gave us no before/after amounts —
 * inventing a dollar figure from a row that has none would be worse than a
 * count.
 *
 * Shared: both the Ad Health tiles (now on Ad Review) and the change-history
 * card (still in the legacy body) render it.
 */
export function budgetHeadline(data: ChangeHistoryResponse): string {
  const rows = data.budget_changes;
  const priced = rows.filter((r) => r.budget_delta != null);
  if (priced.length === 0) {
    return `${rows.length} budget change(s)`;
  }
  const net = priced.reduce((sum, r) => sum + (r.budget_delta ?? 0), 0);
  const campaigns = new Set(priced.map((r) => r.campaign).filter(Boolean)).size;
  const sign = net >= 0 ? '+' : '-';
  const where = campaigns > 0 ? ` across ${campaigns} campaign(s)` : '';
  return `Net daily budget ${sign}$${Math.abs(Math.round(net)).toLocaleString()}${where}, from ${priced.length} change(s)`;
}
